import React, { useState, useEffect, useCallback } from 'react';
import {
  Monitor, Activity, AlertTriangle, TrendingUp, Clock, CheckCircle, XCircle,
  RefreshCw, Search, Filter, Calendar, BarChart3, PieChart, LineChart,
  Eye, Settings, Download, Bell, Zap, GitBranch, Users, MessageSquare
} from 'lucide-react';
import { WorkflowService } from '../../services/WorkflowService';

const WorkflowMonitoring = () => {
  const [executionLogs, setExecutionLogs] = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [optimizationSuggestions, setOptimizationSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('logs'); // 'logs', 'metrics', 'alerts', 'optimization'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterWorkflow, setFilterWorkflow] = useState('all');
  const [dateRange, setDateRange] = useState('24h'); // '1h', '24h', '7d', '30d'
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogDetails, setShowLogDetails] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const workflowService = new WorkflowService();
      const [logsResponse, metricsResponse, alertsResponse, suggestionsResponse] = await Promise.all([
        workflowService.getWorkflowExecutionLogs(),
        workflowService.getWorkflowPerformanceMetrics(),
        workflowService.getWorkflowAlerts(),
        workflowService.getWorkflowOptimizationSuggestions()
      ]);

      if (logsResponse.success) {
        setExecutionLogs(logsResponse.data);
      }
      if (metricsResponse.success) {
        setPerformanceMetrics(metricsResponse.data);
      }
      if (alertsResponse.success) {
        setAlerts(alertsResponse.data);
      }
      if (suggestionsResponse.success) {
        setOptimizationSuggestions(suggestionsResponse.data);
      }
    } catch (err) {
      setError('Failed to fetch workflow monitoring data.');
      console.error('Error fetching workflow monitoring data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleResolveAlert = async (alertId, resolution) => {
    try {
      const workflowService = new WorkflowService();
      const response = await workflowService.resolveWorkflowAlert(alertId, resolution);
      if (response.success) {
        await loadData();
      }
    } catch (err) {
      console.error('Error resolving alert:', err);
    }
  };

  const filteredLogs = executionLogs.filter(log => {
    const matchesSearch = log.workflowName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.ticketId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || log.status === filterStatus;
    const matchesWorkflow = filterWorkflow === 'all' || log.workflowId === filterWorkflow;
    return matchesSearch && matchesStatus && matchesWorkflow;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'running': return <Clock className="w-4 h-4 text-blue-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'failed': return 'text-red-600 bg-red-50';
      case 'running': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 bg-white rounded-lg shadow-md">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600">Loading workflow monitoring data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
            <Monitor className="mr-3" size={28} />
            Workflow Monitoring
          </h1>
          <p className="text-gray-700">
            Monitor workflow execution, performance metrics, and system health.
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
        {[
          { id: 'logs', label: 'Execution Logs', icon: Activity },
          { id: 'metrics', label: 'Performance Metrics', icon: BarChart3 },
          { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
          { id: 'optimization', label: 'Optimization', icon: TrendingUp }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Execution Logs Tab */}
      {activeTab === 'logs' && (
        <div>
          {/* Filters */}
          <div className="flex items-center space-x-4 mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Search className="w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="running">Running</option>
            </select>
            <select
              value={filterWorkflow}
              onChange={(e) => setFilterWorkflow(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Workflows</option>
              {Array.from(new Set(executionLogs.map(log => log.workflowId))).map(id => (
                <option key={id} value={id}>
                  {executionLogs.find(log => log.workflowId === id)?.workflowName}
                </option>
              ))}
            </select>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="1h">Last Hour</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </div>

          {/* Logs Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Workflow
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ticket
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Started
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <GitBranch className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {log.workflowName}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {log.workflowId}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{log.ticketId}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(log.status)}`}>
                          {getStatusIcon(log.status)}
                          <span className="ml-1 capitalize">{log.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDuration(log.duration)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(log.startedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedLog(log);
                            setShowLogDetails(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {filteredLogs.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No execution logs found for the selected filters.
            </div>
          )}
        </div>
      )}

      {/* Performance Metrics Tab */}
      {activeTab === 'metrics' && (
        <div className="space-y-6">
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Activity className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Executions</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {performanceMetrics.totalExecutions || 0}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Success Rate</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {performanceMetrics.successRate || 0}%
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Avg Duration</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatDuration(performanceMetrics.averageDuration || 0)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Failed Executions</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {performanceMetrics.failedExecutions || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Charts Placeholder */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Execution Trends</h3>
              <div className="h-64 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <LineChart className="w-12 h-12 mx-auto mb-2" />
                  <p>Execution trends chart would be displayed here</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Workflow Distribution</h3>
              <div className="h-64 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <PieChart className="w-12 h-12 mx-auto mb-2" />
                  <p>Workflow distribution chart would be displayed here</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-4">
          {alerts.map((alert) => (
            <div key={alert.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <div className={`p-2 rounded-lg mr-4 ${
                    alert.severity === 'critical' ? 'bg-red-100' :
                    alert.severity === 'warning' ? 'bg-yellow-100' :
                    'bg-blue-100'
                  }`}>
                    <AlertTriangle className={`w-5 h-5 ${
                      alert.severity === 'critical' ? 'text-red-600' :
                      alert.severity === 'warning' ? 'text-yellow-600' :
                      'text-blue-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {alert.title}
                    </h3>
                    <p className="text-gray-600 mb-3">{alert.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span>Workflow: {alert.workflowName}</span>
                      <span>Severity: {alert.severity}</span>
                      <span>Triggered: {formatDate(alert.triggeredAt)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleResolveAlert(alert.id, 'acknowledged')}
                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                  >
                    Acknowledge
                  </button>
                  <button
                    onClick={() => handleResolveAlert(alert.id, 'resolved')}
                    className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                  >
                    Resolve
                  </button>
                </div>
              </div>
            </div>
          ))}
          {alerts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No active alerts found.
            </div>
          )}
        </div>
      )}

      {/* Optimization Tab */}
      {activeTab === 'optimization' && (
        <div className="space-y-4">
          {optimizationSuggestions.map((suggestion) => (
            <div key={suggestion.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {suggestion.title}
                    </h3>
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                      suggestion.priority === 'high' ? 'bg-red-100 text-red-800' :
                      suggestion.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {suggestion.priority}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-3">{suggestion.description}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Impact:</span>
                      <span className="ml-1 capitalize">{suggestion.impact}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Effort:</span>
                      <span className="ml-1 capitalize">{suggestion.effort}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Improvement:</span>
                      <span className="ml-1 text-green-600 font-semibold">
                        {suggestion.estimatedImprovement}%
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Workflow:</span>
                      <span className="ml-1">{suggestion.workflowName}</span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600">
                    View Details
                  </button>
                  <button className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600">
                    Apply
                  </button>
                </div>
              </div>
            </div>
          ))}
          {optimizationSuggestions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No optimization suggestions available.
            </div>
          )}
        </div>
      )}

      {/* Log Details Modal */}
      {showLogDetails && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Execution Log Details</h3>
              <button
                onClick={() => setShowLogDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Workflow</label>
                  <p className="text-sm text-gray-900">{selectedLog.workflowName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ticket ID</label>
                  <p className="text-sm text-gray-900">{selectedLog.ticketId}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedLog.status)}`}>
                    {getStatusIcon(selectedLog.status)}
                    <span className="ml-1 capitalize">{selectedLog.status}</span>
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Duration</label>
                  <p className="text-sm text-gray-900">{formatDuration(selectedLog.duration)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Started At</label>
                  <p className="text-sm text-gray-900">{formatDate(selectedLog.startedAt)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Completed At</label>
                  <p className="text-sm text-gray-900">
                    {selectedLog.completedAt ? formatDate(selectedLog.completedAt) : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Steps Executed</label>
                  <p className="text-sm text-gray-900">{selectedLog.stepsExecuted}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Steps Failed</label>
                  <p className="text-sm text-gray-900">{selectedLog.stepsFailed}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Triggered By</label>
                  <p className="text-sm text-gray-900">{selectedLog.triggeredBy}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Executed By</label>
                  <p className="text-sm text-gray-900">{selectedLog.executedBy}</p>
                </div>
              </div>
              {selectedLog.performance && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Performance Metrics</label>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Average Step Time:</span>
                      <span className="ml-1">{selectedLog.performance.averageStepTime}ms</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Memory Usage:</span>
                      <span className="ml-1">{selectedLog.performance.memoryUsage}%</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">CPU Usage:</span>
                      <span className="ml-1">{selectedLog.performance.cpuUsage}%</span>
                    </div>
                  </div>
                </div>
              )}
              {selectedLog.errors && selectedLog.errors.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Errors</label>
                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    {selectedLog.errors.map((error, index) => (
                      <p key={index} className="text-sm text-red-800">{error}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowMonitoring;
